import classnames from 'classnames'
import { FormikProvider, useFormik } from 'formik'
import { TFunction } from 'i18next'
import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import useCreateAsset from '../../../state/mutations/useCreateAsset'
import { FileInputAccept, FileInputFormik } from '../../basics/FileInput/FileInput'
import FormButtons from '../../basics/FormButtons/FormButtons'
import Modal from '../../basics/Modal/Modal'
import ProgressBar from '../../basics/ProgressBar/ProgressBar'
import { TextInputFormik } from '../../basics/TextInput/TextInput'

import styles from './CreateAssetForm.module.scss'
import { useSession } from '../../../state/session'

const acceptedAssetFileTypes = (t: TFunction): FileInputAccept => [
  {
    description: t('basic.fileinput.filetype.image'),
    accept: {
      'image/*': ['.png', '.gif', '.jpeg', '.jpg'],
    },
  },
  {
    description: t('basic.fileinput.filetype.video'),
    accept: {
      'video/*': ['.avi', '.mpg', '.mp4'],
    },
  },
  {
    description: t('basic.fileinput.filetype.audio'),
    accept: {
      'audio/*': ['.wav', '.mp3', '.ogg'],
    },
  },
  {
    description: t('basic.fileinput.filetype.pdf'),
    accept: {
      'application/pdf': ['.pdf'],
    },
  },
  {
    description: t('basic.fileinput.filetype.powerpoint'),
    accept: {
      'application/vnd.ms-powerpoint': ['.ppt'],
      'application/vnd.openxmlformats-officedocument.presentationml.presentation': ['.pptx'],
    },
  },
  {
    description: t('basic.fileinput.filetype.zip'),
    accept: {
      'application/zip': ['.zip'],
      'application/x-zip-compressed': ['.zip'],
    },
  },
]

type CreateAssetFormProps = {
  learningItemUuid: string
  onClose: () => void
}

type CreateAssetProgress = {
  currentFile: string | null
  totalProgress: number
  totalSize: number
  doneSize: number
}

export default React.memo(function CreateAssetForm({ learningItemUuid, onClose }: CreateAssetFormProps) {
  const { t } = useTranslation()
  const { user: currentUser } = useSession()

  const [progress, setProgress] = useState<CreateAssetProgress>({
    currentFile: null,
    totalProgress: 0,
    totalSize: 0,
    doneSize: 0,
  })

  const createAsset = useCreateAsset()
  const formik = useFormik({
    initialValues: {
      learningItemUuid,

      files: [] as File[],
      description: undefined as undefined | string,
      visibleFrom: undefined as undefined | Date,
      visibleUntil: undefined as undefined | Date,
    },
    enableReinitialize: true,
    // validate: (values) => {
    // console.log('VALIDATE', values)
    // },
    onSubmit: async ({ files, ...values }, { setStatus }) => {
      setStatus()
      setProgress({
        currentFile: null,
        totalProgress: 0,
        totalSize: files.reduce((acc, f) => f.size + acc, 0),
        doneSize: 0,
      })
      try {
        let bytesDone = 0
        const onProgress = (e: ProgressEvent) => {
          setProgress((p) => ({
            ...p,
            doneSize: bytesDone + e.loaded,
            totalProgress: (100 * (bytesDone + e.loaded)) / p.totalSize,
          }))
        }

        for (const file of files) {
          setProgress((p) => ({ ...p, currentFile: file.name }))
          await createAsset.mutateAsync({
            ...values,
            filename: file.name,
            mimeType: file.type,
            content: file,
            onProgress,
          })
          bytesDone += file.size
        }

        onClose()
      } catch (err) {
        setStatus(String(err))
      }
    },
  })

  const acceptedFileTypes = useMemo(() => acceptedAssetFileTypes(t), [t])

  return (
    <FormikProvider value={formik}>
      <form noValidate onSubmit={formik.handleSubmit} className={classnames(styles.CreateAssetForm)}>
        <header>
          <hgroup>
            <h1>{t('asset.title create asset')}</h1>
          </hgroup>
        </header>

        <div>
          <FileInputFormik
            name="files"
            label={t('asset.select files')}
            maxFiles={10}
            required
            maxSize={getMaxFileSize(currentUser!.role)}
            types={acceptedFileTypes}
          />
          <TextInputFormik name="description" label={t('asset.field description')} />

          <TextInputFormik name="visibleFrom" label={t('asset.field visibleFrom')} type="date" />

          <TextInputFormik name="visibleUntil" label={t('asset.field visibleUntil')} type="date" />
        </div>

        <FormButtons onClose={onClose} {...formik} isValid={true} />
        {formik.isSubmitting && (
          <Modal className={styles.uploadModal}>
            <div>
              <span>{progress.currentFile || '-'}</span>
            </div>
            <div>
              <ProgressBar progress={progress.totalProgress} />
            </div>
          </Modal>
        )}
      </form>
    </FormikProvider>
  )
})

function getMaxFileSize(userRole: string): number {
  const mb = 1024 * 1024

  switch (userRole) {
    case 'customer_success_manager':
      return 262144 * mb
    default:
      return 60 * mb
  }
}
